import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Phone, Mail, Send, Loader2 } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { Reveal } from "./Reveal";
import { COMPANY } from "./data";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SERVICE_OPTIONS = [
  "Mobile Patrol",
  "Static Guarding",
  "Industrial & Construction Site Security",
  "Access Control",
  "Risk Management & Consulting",
  "Other / Not sure",
];

const inputCls =
  "w-full bg-black border border-white/15 rounded-sm px-4 py-3 text-white placeholder:text-white/35 focus:outline-none focus:border-[color:var(--primary)] transition-colors";

export const Contact = () => {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    company: "",
    service: "",
    message: "",
  });
  const [loading, setLoading] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please fill in your name, email and message.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/quote-requests`, form);
      toast.success("Request received. Our team will be in touch shortly.");
      setForm({ name: "", email: "", phone: "", company: "", service: "", message: "" });
    } catch (err) {
      toast.error("Something went wrong. Please call us instead.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contact" data-testid="contact-section" className="relative py-24 md:py-32">
      <div className="mx-auto max-w-7xl px-5 md:px-8 grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20">
        <Reveal>
          <span className="text-xs font-bold uppercase tracking-[0.24em] text-[color:var(--primary-text)]">
            Get Protected
          </span>
          <h2 className="mt-5 font-display text-4xl md:text-6xl tracking-tight leading-none">
            Request a{" "}
            <span className="font-editorial italic">quote</span>.
          </h2>
          <p className="mt-6 text-lg text-secondary leading-relaxed max-w-md">
            Tell us about your site and security needs. We&apos;ll respond with a
            tailored plan and pricing — no obligation.
          </p>

          <div className="mt-10 space-y-4">
            <a
              href={COMPANY.phoneHref}
              data-testid="contact-phone-link"
              className="flex items-center gap-4 group"
            >
              <span className="inline-flex h-12 w-12 items-center justify-center rounded-sm border border-white/10 bg-[color:var(--surface)] text-[color:var(--primary-text)] group-hover:border-[color:var(--primary)]/50 transition-colors">
                <Phone size={20} />
              </span>
              <div>
                <div className="text-xs uppercase tracking-widest text-secondary">Call us</div>
                <div className="font-display text-xl tracking-tight">{COMPANY.phone}</div>
              </div>
            </a>
            <a
              href={`mailto:${COMPANY.email}`}
              data-testid="contact-email-link"
              className="flex items-center gap-4 group"
            >
              <span className="inline-flex h-12 w-12 items-center justify-center rounded-sm border border-white/10 bg-[color:var(--surface)] text-[color:var(--primary-text)] group-hover:border-[color:var(--primary)]/50 transition-colors">
                <Mail size={20} />
              </span>
              <div>
                <div className="text-xs uppercase tracking-widest text-secondary">Email us</div>
                <div className="font-display text-xl tracking-tight break-all">{COMPANY.email}</div>
              </div>
            </a>
          </div>
        </Reveal>

        <Reveal delay={0.1}>
          <form
            onSubmit={submit}
            data-testid="quote-form"
            className="bg-[color:var(--surface)] border border-white/10 rounded-sm p-6 md:p-8 space-y-5"
          >
            <div className="grid sm:grid-cols-2 gap-5">
              <div>
                <label className="block text-sm text-secondary mb-2">Full name *</label>
                <input data-testid="input-name" className={inputCls} value={form.name} onChange={set("name")} placeholder="Jane Doe" />
              </div>
              <div>
                <label className="block text-sm text-secondary mb-2">Email *</label>
                <input data-testid="input-email" type="email" className={inputCls} value={form.email} onChange={set("email")} placeholder="you@company.com" />
              </div>
            </div>
            <div className="grid sm:grid-cols-2 gap-5">
              <div>
                <label className="block text-sm text-secondary mb-2">Phone</label>
                <input data-testid="input-phone" className={inputCls} value={form.phone} onChange={set("phone")} placeholder="(604) 000-0000" />
              </div>
              <div>
                <label className="block text-sm text-secondary mb-2">Company</label>
                <input data-testid="input-company" className={inputCls} value={form.company} onChange={set("company")} placeholder="Optional" />
              </div>
            </div>
            <div>
              <label className="block text-sm text-secondary mb-2">Service needed</label>
              <Select value={form.service} onValueChange={(v) => setForm((f) => ({ ...f, service: v }))}>
                <SelectTrigger
                  data-testid="select-service"
                  className="w-full bg-black border-white/15 rounded-sm px-4 py-6 text-white focus:ring-0 focus:border-[color:var(--primary)]"
                >
                  <SelectValue placeholder="Select a service" />
                </SelectTrigger>
                <SelectContent className="bg-[#0f0f0f] border-white/15 text-white">
                  {SERVICE_OPTIONS.map((s) => (
                    <SelectItem key={s} value={s} data-testid={`service-option-${s}`} className="focus:bg-[color:var(--primary)]/20">
                      {s}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="block text-sm text-secondary mb-2">How can we help? *</label>
              <textarea data-testid="input-message" rows={4} className={inputCls} value={form.message} onChange={set("message")} placeholder="Tell us about your site, location and requirements..." />
            </div>
            <button
              type="submit"
              disabled={loading}
              data-testid="submit-quote-button"
              className="w-full inline-flex items-center justify-center gap-2 bg-[color:var(--primary)] hover:bg-[color:var(--primary-hover)] disabled:opacity-60 text-white font-semibold px-7 py-4 rounded-sm transition-colors"
            >
              {loading ? <Loader2 size={18} className="animate-spin" /> : <Send size={18} />}
              {loading ? "Sending..." : "Send Request"}
            </button>
          </form>
        </Reveal>
      </div>
    </section>
  );
};
